import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, AlertTriangle, Loader2, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtD = (d) => {
  if (!d) return '';
  try { return new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return d; }
};

// Confirm box: cancel a job (schedule work period and/or dispatch request),
// text the crew + PM + dispatcher with an optional note.
export const CancelJobModal = ({ requestId, workPeriodId, onClose, onCancelled }) => {
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams();
    if (requestId) params.set('request_id', requestId);
    if (workPeriodId) params.set('work_period_id', workPeriodId);
    axios.get(`${API}/dispatch/cancel-job/preview?${params}`, authHeader())
      .then((r) => setPreview(r.data))
      .catch((e) => setErr(e.response?.data?.detail || 'Could not load job details'));
  }, [requestId, workPeriodId]);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/dispatch/cancel-job`,
        { request_id: requestId, work_period_id: workPeriodId, note }, authHeader());
      const d = r.data;
      if (d.texts_sent > 0) {
        toast.success(`Job cancelled — text sent to ${d.texts_sent} ${d.texts_sent === 1 ? 'person' : 'people'}${d.failed ? ` (${d.failed} failed)` : ''}`);
      } else {
        toast.warning('Job cancelled, but nobody had a valid phone number to text');
      }
      onCancelled();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel job');
      setBusy(false);
    }
  };

  const job = preview?.job;
  const recipients = preview?.recipients || [];
  const dates = job ? (job.end_date && job.end_date !== job.start_date
    ? `${fmtD(job.start_date)} – ${fmtD(job.end_date)}` : fmtD(job.start_date)) : '';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4" data-testid="cancel-job-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-red-700 flex items-center gap-2">
            <AlertTriangle size={20} /> Cancel Job &amp; Notify Crew
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-gray-700" data-testid="cancel-job-close"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          {err ? (
            <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</p>
          ) : !preview ? (
            <div className="flex items-center justify-center py-8 text-slate-500"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>
          ) : (
            <>
              <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm" data-testid="cancel-job-summary">
                <p className="font-bold text-gray-900">WO {job.work_order_number}{job.customer ? ` — ${job.customer}` : ''}</p>
                {dates && <p className="text-gray-500 mt-0.5">Scheduled: {dates}</p>}
              </div>

              {preview.already_cancelled && (
                <p className="text-sm text-amber-700 bg-amber-50 rounded-md px-3 py-2">This job is already marked cancelled.</p>
              )}

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                  <MessageSquare size={13} /> Who gets the text ({recipients.length})
                </p>
                {recipients.length === 0 ? (
                  <p className="text-sm text-amber-600">Nobody has a valid phone number — the job will still be marked cancelled.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5" data-testid="cancel-job-recipients">
                    {recipients.map((r, i) => (
                      <span key={i} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded border ${r.role === 'crew' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                        {r.name}{r.role !== 'crew' && <span className="opacity-60">({r.role === 'pm' ? 'PM' : 'dispatch'} FYI)</span>}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note to include (optional)</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder={'e.g. Rescheduled to Sept 7th — we\'ll re-dispatch closer to the date'}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none resize-none"
                  data-testid="cancel-job-note"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Text reads: "JOB CANCELLED — WO {job.work_order_number}{dates ? ` … Was scheduled: ${dates}` : ''}{note.trim() ? ` … Note: ${note.trim()}` : ''}"
                </p>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} disabled={busy} className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100" data-testid="cancel-job-keep">Keep Job</button>
          <button
            onClick={confirm}
            disabled={busy || !preview || preview.already_cancelled}
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
            data-testid="cancel-job-confirm"
          >
            {busy ? <Loader2 className="animate-spin" size={15} /> : <AlertTriangle size={15} />}
            {busy ? 'Cancelling…' : 'Cancel Job & Send Text'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelJobModal;

// Pick a new start date for a cancelled job and copy it there in one tap.
// End date defaults to keeping the same job length.
export const RebookJobModal = ({ requestId, workPeriodId, jobLabel, onClose, onRebooked }) => {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (!start) { toast.error('Pick the new start date'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/dispatch/rebook-job`,
        { request_id: requestId, work_period_id: workPeriodId, start_date: start, end_date: end || undefined },
        authHeader());
      const d = r.data;
      const range = d.end_date && d.end_date !== d.start_date ? `${fmtD(d.start_date)} – ${fmtD(d.end_date)}` : fmtD(d.start_date);
      toast.success(d.request ? `Re-booked as ${d.request.request_number} for ${range}` : `Re-booked for ${range}`);
      onRebooked(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to re-book job');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4" data-testid="rebook-job-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-emerald-700">Re-book Job</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-gray-700" data-testid="rebook-close"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          {jobLabel && <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">{jobLabel}</p>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New start date *</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
              data-testid="rebook-start" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New end date <span className="text-slate-500 font-normal">(optional)</span></label>
            <input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
              data-testid="rebook-end" />
            <p className="text-xs text-slate-500 mt-1">Leave blank to keep the same job length. Same crew, equipment and tools are copied over.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} disabled={busy} className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={confirm} disabled={busy || !start}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
            data-testid="rebook-confirm">
            {busy ? <Loader2 className="animate-spin" size={15} /> : null}
            {busy ? 'Re-booking…' : 'Re-book Job'}
          </button>
        </div>
      </div>
    </div>
  );
};
